import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  RefreshCw,
  MessageSquare,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Camera,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";

interface Conversation {
  id: string;
  created_at: string;
  updated_at: string;
  user_name: string | null;
  user_email: string | null;
  user_phone: string | null;
  user_type: string | null;
  wedding_date: string | null;
  venue: string | null;
  services: string[] | null;
  summary_sent: boolean;
}

interface ChatMessage {
  id: string;
  role: string;
  content: string;
  created_at: string;
}

export function ChatHistoryManager() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      // First, get conversation IDs that have at least one user message
      const { data: userMessages, error: msgError } = await supabase
        .from("chat_messages")
        .select("conversation_id")
        .eq("role", "user");

      if (msgError) throw msgError;

      // Get unique conversation IDs
      const conversationIds = [...new Set(
        userMessages?.map(m => m.conversation_id) || []
      )];

      if (conversationIds.length === 0) {
        setConversations([]);
        return;
      }

      // Fetch only conversations with user messages
      const { data, error } = await supabase
        .from("chat_conversations")
        .select("*")
        .in("id", conversationIds)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error: any) {
      console.error("Error fetching conversations:", error);
      toast({
        title: "Error loading conversations",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error: any) {
      console.error("Error fetching messages:", error);
      toast({
        title: "Error loading messages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleViewConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    await fetchMessages(conversation.id);
  };

  const closeDialog = () => {
    setSelectedConversation(null);
    setMessages([]);
  };

  const deleteConversation = async () => {
    if (!conversationToDelete) return;
    
    setIsDeleting(true);
    try {
      // Delete messages first (cascade should handle this, but being explicit)
      const { error: messagesError } = await supabase
        .from("chat_messages")
        .delete()
        .eq("conversation_id", conversationToDelete.id);

      if (messagesError) throw messagesError;

      // Delete quick reply analytics for this conversation
      await supabase
        .from("quick_reply_analytics")
        .delete()
        .eq("conversation_id", conversationToDelete.id);

      // Delete the conversation
      const { error: conversationError } = await supabase
        .from("chat_conversations")
        .delete()
        .eq("id", conversationToDelete.id);

      if (conversationError) throw conversationError;

      toast({
        title: "Conversation deleted",
        description: "The conversation and all messages have been removed.",
      });

      // Refresh the list
      await fetchConversations();
    } catch (error: any) {
      console.error("Error deleting conversation:", error);
      toast({
        title: "Error deleting conversation",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConversationToDelete(null);
    }
  };

  // Stats
  const totalConversations = conversations.length;
  const withContact = conversations.filter(c => c.user_email || c.user_phone).length;
  const prospects = conversations.filter(c => c.user_type === "prospect").length;
  const clients = conversations.filter(c => c.user_type === "client").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            Chat Conversations
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            View chatbot conversations and extracted user data
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchConversations}
          disabled={isLoading}
          size="sm"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Total Chats</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{totalConversations}</p>
        </div>
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">With Contact Info</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{withContact}</p>
        </div>
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Prospects</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{prospects}</p>
        </div>
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Existing Clients</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{clients}</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-card border border-border overflow-hidden">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
            <p>No chat conversations yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Wedding Date</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversations.map((conversation) => (
                  <TableRow key={conversation.id}>
                    <TableCell className="whitespace-nowrap">
                      <div className="text-sm">
                        {format(new Date(conversation.created_at), "MMM d, yyyy")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(conversation.created_at), "h:mm a")}
                      </div>
                    </TableCell>
                    <TableCell>
                      {conversation.user_name ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{conversation.user_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {conversation.user_email && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3" />
                            <a
                              href={`mailto:${conversation.user_email}`}
                              className="hover:text-primary"
                            >
                              {conversation.user_email}
                            </a>
                          </div>
                        )}
                        {conversation.user_phone && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            {conversation.user_phone}
                          </div>
                        )}
                        {!conversation.user_email && !conversation.user_phone && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {conversation.user_type ? (
                        <Badge
                          variant="secondary"
                          className={
                            conversation.user_type === "prospect"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }
                        >
                          {conversation.user_type}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {conversation.wedding_date ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(conversation.wedding_date), "MMM d, yyyy")}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {conversation.services?.map((service) => (
                          <Badge key={service} variant="outline" className="text-xs">
                            {service}
                          </Badge>
                        )) || <span className="text-muted-foreground">—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConversation(conversation)}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          View
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConversationToDelete(conversation)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Conversation Dialog */}
      <Dialog open={!!selectedConversation} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Conversation
              {selectedConversation?.user_name && (
                <span className="text-muted-foreground font-normal">
                  with {selectedConversation.user_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* User Info Summary */}
          {selectedConversation && (
            <div className="flex flex-wrap gap-3 p-3 bg-muted/50 rounded-lg text-sm">
              {selectedConversation.user_name && (
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedConversation.user_name}
                </div>
              )}
              {selectedConversation.user_email && (
                <div className="flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedConversation.user_email}
                </div>
              )}
              {selectedConversation.user_phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedConversation.user_phone}
                </div>
              )}
              {selectedConversation.wedding_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(selectedConversation.wedding_date), "MMM d, yyyy")}
                </div>
              )}
              {selectedConversation.venue && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedConversation.venue}
                </div>
              )}
              {selectedConversation.services && selectedConversation.services.length > 0 && (
                <div className="flex items-center gap-1">
                  <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedConversation.services.join(", ")}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-[400px]">
              {isLoadingMessages ? (
                <div className="flex h-40 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-40 items-center justify-center text-muted-foreground">
                  No messages found
                </div>
              ) : (
                <div className="space-y-4 py-4 pr-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {format(new Date(message.created_at), "h:mm a")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!conversationToDelete} onOpenChange={() => setConversationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation? This will permanently remove all messages and extracted data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConversation}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
